<script setup lang="ts">
    import { __ } from '@/js/stores/expressions';
    import EchoButton from '../EchoButton.vue';
    import IconButton from '../IconButton.vue';
    import {
        InteractionTableStoreName,
        useInteractionTableStoreByName,
    } from '@/js/stores/interactions-table';
    import { computed, onBeforeUnmount, onMounted, reactive, watch } from 'vue';
    import { ChevronLeftIcon, ChevronRightIcon, PlusIcon } from '@lucide/vue';
    import {
        getDataTableFrozenColumn,
        getDataTableScrollContainer,
        SCROLL_EDGE_TOLERANCE_PX,
    } from '@/js/utils/dataTableDom';

    const FALLBACK_SCROLL_STEP_PX = 160;
    const CONTINUOUS_SCROLL_HOLD_DELAY_MS = 250;
    const CONTINUOUS_SCROLL_STEP_PX = 12;

    const props = withDefaults(
        defineProps<{
            tableStoreName: InteractionTableStoreName;
            addColumn?: boolean;
            showRowActions?: boolean;
            containerRef?: HTMLElement | null;
        }>(),
        {
            addColumn: true,
            showRowActions: true,
            containerRef: null,
        }
    );

    defineEmits<{
        (e: 'showColumnModal'): void;
    }>();

    const tableStore = useInteractionTableStoreByName(props.tableStoreName);

    const isProtocolTable = computed(
        () => props.tableStoreName === InteractionTableStoreName.ProtocolTable
    );

    const rowsExpandCollapseLabel = computed(() => {
        if (isProtocolTable.value) {
            return tableStore.allRowsExpansionActive
                ? __('table-protocol-collapse-rows')
                : __('table-protocol-expand-rows');
        }

        return tableStore.allRowsExpansionActive
            ? __('interactions-table-collapse-all')
            : __('interactions-table-expand-all');
    });

    const columnsExpandCollapseLabel = computed(() => {
        if (isProtocolTable.value) {
            return tableStore.allColumnsExpanded
                ? __('table-protocol-collapse-columns')
                : __('table-protocol-expand-columns');
        }

        return tableStore.showSummary
            ? __('interactions-table-hide-summary')
            : __('interactions-table-show-summary');
    });

    const scrollState = reactive({ canScrollLeft: false, canScrollRight: false });

    const updateScrollState = (
        scrollContainer: HTMLElement | null = getDataTableScrollContainer(props.containerRef)
    ) => {
        if (!scrollContainer) {
            scrollState.canScrollLeft = false;
            scrollState.canScrollRight = false;
            return;
        }

        scrollState.canScrollLeft = scrollContainer.scrollLeft > SCROLL_EDGE_TOLERANCE_PX;
        scrollState.canScrollRight =
            scrollContainer.scrollLeft + scrollContainer.clientWidth <
            scrollContainer.scrollWidth - SCROLL_EDGE_TOLERANCE_PX;
    };

    // Measured fresh on every click: columns can resize (window resize, reorder,
    // expand/collapse) and aren't all the same width under table-layout: auto, so
    // the column at the scroll boundary is used instead of always the first one.
    const getColumnScrollStep = (direction: -1 | 1) => {
        const container = getDataTableScrollContainer(props.containerRef);
        const frozenColumn = getDataTableFrozenColumn(container);
        if (!container || !frozenColumn) return FALLBACK_SCROLL_STEP_PX;

        const frozenRight = frozenColumn.getBoundingClientRect().right;
        const columns = container.querySelectorAll<HTMLElement>(
            'thead th:not([data-p-frozen-column="true"])'
        );

        let previousColumnLeft: number | null = null;
        for (const column of columns) {
            const rect = column.getBoundingClientRect();
            if (rect.right > frozenRight + SCROLL_EDGE_TOLERANCE_PX) {
                // Scrolling right: part of this column may already be hidden behind
                // the frozen column from an earlier click, so only its visible sliver
                // should move, its full width would overshoot into the next column.
                if (direction > 0) return rect.right - frozenRight;

                // Scrolling left: reveals the column just behind the frozen column,
                // only up to its own left edge, not the full width of everything
                // currently hidden there.
                return previousColumnLeft === null
                    ? FALLBACK_SCROLL_STEP_PX
                    : frozenRight - previousColumnLeft;
            }
            previousColumnLeft = rect.left;
        }

        return FALLBACK_SCROLL_STEP_PX;
    };

    const scrollBy = (direction: -1 | 1) => {
        getDataTableScrollContainer(props.containerRef)?.scrollBy({
            left: direction * getColumnScrollStep(direction),
            behavior: 'smooth',
        });
    };

    let holdTimeout: ReturnType<typeof setTimeout> | null = null;
    let continuousScrollFrame: number | null = null;
    let wasHolding = false;

    const stopContinuousScroll = () => {
        if (holdTimeout !== null) {
            clearTimeout(holdTimeout);
            holdTimeout = null;
        }
        if (continuousScrollFrame !== null) {
            cancelAnimationFrame(continuousScrollFrame);
            continuousScrollFrame = null;
        }
    };

    const runContinuousScroll = (direction: -1 | 1) => {
        const canContinue = direction < 0 ? scrollState.canScrollLeft : scrollState.canScrollRight;
        if (!canContinue) {
            stopContinuousScroll();
            return;
        }

        getDataTableScrollContainer(props.containerRef)?.scrollBy({
            left: direction * CONTINUOUS_SCROLL_STEP_PX,
        });
        continuousScrollFrame = requestAnimationFrame(() => runContinuousScroll(direction));
    };

    /**
     * A short click still jumps one column via the button's own @click; holding past
     * the delay switches to a continuous flow instead, so the two don't fight over the scroll.
     */
    const startHold = (direction: -1 | 1) => {
        stopContinuousScroll();
        wasHolding = false;
        holdTimeout = setTimeout(() => {
            wasHolding = true;
            runContinuousScroll(direction);
        }, CONTINUOUS_SCROLL_HOLD_DELAY_MS);
    };

    const endHold = () => {
        stopContinuousScroll();
    };

    /**
     * No click follows a mouseleave, so wasHolding resets here; a later keyboard
     * activation (Enter/Space, which skips mousedown) would otherwise still find
     * it set and get ignored.
     */
    const handleMouseLeave = () => {
        stopContinuousScroll();
        wasHolding = false;
    };

    /**
     * mouseup after a hold still fires the button's native click; skip the single-column
     * jump then, it already scrolled continuously during the hold.
     */
    const handleScrollClick = (direction: -1 | 1) => {
        if (wasHolding) {
            wasHolding = false;
            return;
        }

        scrollBy(direction);
    };

    watch(
        () => [props.containerRef, tableStore.visibleColumns.length] as const,
        () => updateScrollState(),
        { immediate: true, flush: 'post' }
    );

    // Expanding/collapsing a column group changes the rendered table width without
    // changing visibleColumns.length (child columns keep a non-null order throughout).
    watch(
        () => tableStore.expandedColumns,
        () => updateScrollState(),
        {
            deep: true,
            flush: 'post',
        }
    );

    // The scroll event's own target is already the scrolled element, reused here
    // instead of a fresh querySelector on every scroll.
    const onContainerScroll = (e: Event) => updateScrollState(e.target as HTMLElement | null);

    watch(
        () => props.containerRef,
        (container, _oldContainer, onCleanup) => {
            // Capture phase catches scroll events from the inner scroll container even
            // after PrimeVue remounts it (e.g. on column reorder).
            container?.addEventListener('scroll', onContainerScroll, true);
            onCleanup(() => container?.removeEventListener('scroll', onContainerScroll, true));
        },
        { immediate: true }
    );

    const onWindowResize = () => updateScrollState();
    onMounted(() => window.addEventListener('resize', onWindowResize));
    onBeforeUnmount(() => {
        window.removeEventListener('resize', onWindowResize);
        stopContinuousScroll();
    });
</script>
<template>
    <div
        class="w-full flex items-center gap-3 pl-6 pr-3 py-4 bg-primary-plum-50 border-b border-gray-light"
    >
        <div v-if="showRowActions" class="flex items-center gap-3.5">
            <EchoButton
                size="small"
                variant="secondary"
                @click="
                    (e: MouseEvent) => {
                        tableStore.toggleAllRowsExpansion();
                        (e.currentTarget as HTMLElement)?.blur();
                    }
                "
            >
                {{ rowsExpandCollapseLabel }}
            </EchoButton>

            <div
                :class="{
                    'cursor-default': !isProtocolTable && tableStore.allRowsExpansionActive,
                }"
            >
                <EchoButton
                    size="small"
                    variant="secondary"
                    :disabled="!isProtocolTable && tableStore.allRowsExpansionActive"
                    @click="
                        (e: MouseEvent) => {
                            if (isProtocolTable) {
                                tableStore.toggleAllColumnsExpansion();
                            } else {
                                tableStore.toggleShowSummary();
                            }

                            (e.currentTarget as HTMLElement)?.blur();
                        }
                    "
                >
                    {{ columnsExpandCollapseLabel }}
                </EchoButton>
            </div>
        </div>

        <div
            v-if="addColumn"
            class="flex items-center gap-3.5 pl-4 ml-1 border-l border-gray-light"
        >
            <EchoButton variant="secondary" size="small" @click="$emit('showColumnModal')">
                {{ __('interactions-table-add-column') }}
                <template #icon-right><PlusIcon /></template>
            </EchoButton>
        </div>

        <div class="flex items-center gap-1 pl-4 ml-auto border-l border-gray-light">
            <IconButton
                area-size="size-7"
                size="size-4"
                :disabled="!scrollState.canScrollLeft"
                :aria-label="__('table-scroll-left-label')"
                :tooltip="__('table-scroll-left-label')"
                @mousedown="startHold(-1)"
                @mouseup="endHold"
                @mouseleave="handleMouseLeave"
                @click="handleScrollClick(-1)"
            >
                <ChevronLeftIcon class="size-4" />
            </IconButton>
            <IconButton
                area-size="size-7"
                size="size-4"
                :disabled="!scrollState.canScrollRight"
                :aria-label="__('table-scroll-right-label')"
                :tooltip="__('table-scroll-right-label')"
                @mousedown="startHold(1)"
                @mouseup="endHold"
                @mouseleave="handleMouseLeave"
                @click="handleScrollClick(1)"
            >
                <ChevronRightIcon class="size-4" />
            </IconButton>
        </div>
    </div>
</template>
